// Tooltip — hover/focus-triggered descriptive bubble.
//
// Lightweight by design. Shows on pointer-enter / focus-in, hides on
// pointer-leave / focus-out / Esc. No portal: the bubble is positioned
// absolutely above the target inside the same wrapper. For dense pages
// this is enough — if a page needs richer behaviour (delays, follow-cursor,
// portal escape from overflow:hidden), build that on top of Modal/Drawer
// rather than expanding this component.

import { useId, useState, type ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  /** Tooltip body. Strings or short rich content; not for paragraphs. */
  content: ReactNode;
  /** The element the tooltip describes. Should be a single focusable child. */
  children: ReactNode;
  /** When true, the tooltip never shows. */
  disabled?: boolean;
  className?: string;
}

export function Tooltip({ content, children, disabled = false, className }: TooltipProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const id = useId();

  if (disabled) {
    return <span className={cn(styles.wrap, className)}>{children}</span>;
  }

  return (
    <span
      className={cn(styles.wrap, className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <span className={styles.target} aria-describedby={open ? id : undefined} tabIndex={0}>
        {children}
      </span>
      <span
        id={id}
        role="tooltip"
        className={cn(styles.bubble, open && styles.bubbleVisible)}
        aria-hidden={!open}
      >
        {content}
      </span>
    </span>
  );
}
